import { useCallback, useState } from 'react'
import useSWR from 'swr'

import { useHydrated } from '@/hooks/use-hydrated'
import { useAuthStore } from '@/store/auth'

import {
  changePasswordMutation,
  ChangePasswordRequest,
  uploadProfileIdDocument,
  uploadProfileLicense
} from '@/api/auth'
import { API_BASE } from '@/api/endpoints'

export type ProfileFile = {
  last_modified: number
  mime_type: string
  name: string
  path: string
  size: number
}

type ProfileFilesResponse = {
  files: ProfileFile[]
}

type UploadResult = {
  message: string
  path?: string
}

type UseDocumentUploadOptions = {
  onSuccess?: (_result: UploadResult) => void
  onError?: (_error: Error) => void
}

/**
 * Hook per gestire l'upload del documento di licenza
 */
export const useUploadLicense = (options?: UseDocumentUploadOptions) => {
  const [isUploading, setIsUploading] = useState(false)
  const token = useAuthStore((state) => state.token)

  const uploadLicense = useCallback(
    async (file: File): Promise<{ path: string }> => {
      if (!token) {
        throw new Error('Utente non autenticato')
      }

      setIsUploading(true)
      try {
        const result = await uploadProfileLicense(token, file)
        options?.onSuccess?.(result)
        return { path: result.path || '' }
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Errore durante il caricamento')
        options?.onError?.(err)
        throw err
      } finally {
        setIsUploading(false)
      }
    },
    [token, options]
  )

  return {
    uploadLicense,
    isUploading
  }
}

/**
 * Hook per gestire l'upload del documento d'identità
 */
export const useUploadIdDocument = (options?: UseDocumentUploadOptions) => {
  const [isUploading, setIsUploading] = useState(false)
  const token = useAuthStore((state) => state.token)

  const uploadIdDocument = useCallback(
    async (file: File): Promise<{ path: string }> => {
      if (!token) {
        throw new Error('Utente non autenticato')
      }

      setIsUploading(true)
      try {
        const result = await uploadProfileIdDocument(token, file)
        options?.onSuccess?.(result)
        return { path: result.path || '' }
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Errore durante il caricamento')
        options?.onError?.(err)
        throw err
      } finally {
        setIsUploading(false)
      }
    },
    [token, options]
  )

  return {
    uploadIdDocument,
    isUploading
  }
}

/**
 * Hook per gestire il cambio password
 */
type UseChangePasswordOptions = {
  onSuccess?: () => void
  onError?: (_error: Error) => void
}

export const useChangePassword = (options?: UseChangePasswordOptions) => {
  const [isChanging, setIsChanging] = useState(false)
  const token = useAuthStore((state) => state.token)

  const changePassword = useCallback(
    async (data: ChangePasswordRequest): Promise<void> => {
      if (!token) {
        throw new Error('Utente non autenticato')
      }

      setIsChanging(true)
      try {
        await changePasswordMutation(token, data)
        options?.onSuccess?.()
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Errore durante il cambio password')
        options?.onError?.(err)
        throw err
      } finally {
        setIsChanging(false)
      }
    },
    [token, options]
  )

  return {
    changePassword,
    isChanging
  }
}

/**
 * Hook per ottenere la lista dei file del profilo
 */
export const useProfileFiles = () => {
  const token = useAuthStore((state) => state.token)
  const isHydrated = useHydrated()

  const fetcher = async (url: string) => {
    if (!token) throw new Error('Utente non autenticato')

    const res = await fetch(url, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`
      }
    })

    if (!res.ok) {
      throw new Error('Errore nel caricamento dei file')
    }

    return res.json()
  }

  const { data, error, isLoading, mutate } = useSWR<ProfileFilesResponse>(
    isHydrated && token ? `${API_BASE}/profile/files` : null,
    fetcher
  )

  return {
    files: data?.files ?? [],
    isLoading,
    error,
    mutate
  }
}

/**
 * Hook per scaricare un file del profilo
 */
export const useDownloadProfileFile = () => {
  const [isDownloading, setIsDownloading] = useState(false)
  const token = useAuthStore((state) => state.token)

  const downloadFile = useCallback(
    async (filename: string) => {
      if (!token) {
        throw new Error('Utente non autenticato')
      }

      setIsDownloading(true)
      try {
        const res = await fetch(`${API_BASE}/profile/files/${filename}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/octet-stream'
          }
        })

        if (!res.ok) {
          throw new Error('Errore nel download del file')
        }

        const blob = await res.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      } finally {
        setIsDownloading(false)
      }
    },
    [token]
  )

  return {
    downloadFile,
    isDownloading
  }
}
